import React from 'react';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants';
import { useIntl } from '@atlassian/jira-intl';
import { useIsArchived, useIssueKey } from '@atlassian/jira-issue-context-service';
import { useProjectType } from '@atlassian/jira-project-context-service';
import {
	PROJECT_ACTIVE,
	PROJECT_ARCHIVED,
} from '@atlassian/jira-project-context-service/src/constants';
import { useProjectKey, useProjectContext } from '@atlassian/jira-project-context-service/src/main';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller';
import { useUnarchiveIssueModalActions } from '@atlassian/jira-unarchive-modal';
import { isArchivedIssuesPageLinkEnabled, isIssueUnarchivalButtonEnabled } from './feature-flags';
import messages from './messages';

export const Header = ({ title }: { title: string }) => <h3>{title}</h3>;
export const MainContent = ({ message }: { message: string }) => <p>{message}</p>;
export const ArchivedProjectsLink = ({ text }: { text: string }) => (
	<p>
		<a href="/jira/settings/projects/archive">{text}</a>
	</p>
);
/**
 * TODO: This component contains a sample link to archived issues.
 * Replace with actual endpoint
 */

export const ArchivedIssuesViewLink = ({ text }: { text: string }) => (
	<SectionMessageAction href="/sample-link/Archived-Issues">{text}</SectionMessageAction>
);

export const ArchivedIssuesRestoreLink = ({ text }: { text: string }) => {
	const { openUnarchiveIssueModal } = useUnarchiveIssueModalActions();
	const issueKey = useIssueKey();
	return (
		<>
			<SectionMessageAction
				href="#"
				onClick={() => {
					openUnarchiveIssueModal(issueKey);
				}}
			>
				{text}
			</SectionMessageAction>
		</>
	);
};

export const ReadOnlyIssueInfo = () => {
	const { formatMessage } = useIntl();
	const isAdminUser = useIsAdmin();
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey);
	const projectContext = useProjectContext(projectKey);
	const isArchived = useIsArchived();

	if (projectContext?.projectStatus === PROJECT_ACTIVE && isArchived) {
		if (!isIssueUnarchivalButtonEnabled()) {
			if (isArchivedIssuesPageLinkEnabled() && isAdminUser) {
				return (
					<SectionMessage
						appearance="information"
						actions={[
							<ArchivedIssuesViewLink
								key="achived-issues-view-link"
								text={formatMessage(messages.readOnlyArchivedIssueLinkDefault)}
							/>,
						]}
					>
						<Header title={formatMessage(messages.readOnlyArchivedIssueTitle)} />
						<MainContent message={formatMessage(messages.readOnlyArchivedIssueMessageDefault)} />
					</SectionMessage>
				);
			}

			return (
				<SectionMessage appearance="information">
					<Header title={formatMessage(messages.readOnlyArchivedIssueTitle)} />
					<MainContent message={formatMessage(messages.readOnlyArchivedIssueMessageDefault)} />
				</SectionMessage>
			);
		}

		if (isAdminUser) {
			return (
				<SectionMessage
					appearance="information"
					actions={[
						<ArchivedIssuesViewLink
							key="achived-issues-view-link"
							text={formatMessage(messages.readOnlyArchivedIssueLink)}
						/>,
						<ArchivedIssuesRestoreLink
							key="restore-archived-issue-button"
							text={formatMessage(messages.readOnlyArchivedIssueRestoreLink)}
						/>,
					]}
				>
					<MainContent message={formatMessage(messages.readOnlyArchivedIssueMessageAdminUser)} />
				</SectionMessage>
			);
		}

		return (
			<SectionMessage appearance="information">
				<MainContent message={formatMessage(messages.readOnlyArchivedIssueMessageNonAdminUser)} />
			</SectionMessage>
		);
	}

	if (projectContext?.projectStatus !== PROJECT_ARCHIVED) {
		return null;
	}

	const isServiceDeskProject = projectType === SERVICE_DESK_PROJECT;
	const message = isServiceDeskProject
		? formatMessage(messages.jsmReadOnlyIssueMessage)
		: formatMessage(messages.readOnlyIssueMessage);

	return (
		<SectionMessage appearance="information">
			<Header title={formatMessage(messages.readOnlyIssueTitle)} />
			<MainContent message={message} />
			{isAdminUser ? (
				<ArchivedProjectsLink text={formatMessage(messages.readOnlyIssueLink)} />
			) : null}
		</SectionMessage>
	);
};
